import { API_BASE_URL } from "@/lib/env";
import {
  clearAuthTokens,
  readAuthToken,
  readOrganizationId,
  readPreviewSubject,
  readRefreshToken,
  writeAuthToken,
  writeRefreshToken,
} from "@/auth/auth-storage";

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  rawBody?: string;
  contentType?: string;
  signal?: AbortSignal;
  responseType?: "blob";
};

export type CsvImportResult = { created: number; updated: number; total: number };
export type GridImportKind = "feeders" | "substations";
export type GridImportReview = {
  fingerprint: string | null;
  errors: { row: number; message: string }[];
  errors_truncated: boolean;
  rows: {
    row: number;
    external_key: string;
    company_key: string;
    substation_key: string | null;
    action: "create" | "update";
    changes: Record<string, { before: unknown; after: unknown }>;
  }[];
};

export type AnalyticsFeeder = {
  dev_eui: string;
  program_id: string;
  program_name: string;
  vendor: string;
  disco: string;
  state: string;
  substation: string;
  voltage_class: string;
  band: string;
  feeder_name: string;
  device_id: string;
  controller_name: string;
  slot: number;
  modbus_id: number | null;
  feeder_id?: number | null;
  station_type?: string | null;
  voltage_kv?: string | null;
  feeder_location?: string | null;
  major_streets?: string | null;
  served_states?: string[];
  regulators?: string[];
  nerc_service_classes?: string[];
  metered_customers?: number | null;
  unmetered_customers?: number | null;
  iot_phase?: string | null;
  iot_installed?: boolean | null;
  source_reference?: string | null;
  legacy_substation?: string | null;
};

export type GrafanaReport = "executive" | "performance" | "reliability" | "daily-dar" | "help";
export type ReportGrouping = "Overall" | "DISCO" | "Substation";
export type GrafanaReportFilters = {
  program: string;
  vendor: string;
  disco: string;
  controller?: string;
  state: string;
  substation: string;
  voltage_class: string;
  band: string;
  feeder: string;
  station_type?: string;
  regulator?: string;
  nerc_class?: string;
  phase?: string;
  status?: string;
  groupby: ReportGrouping;
  from: string;
  to: string;
};

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function readErrorDetail(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null) {
      const detail: unknown = (body as Record<string, unknown>).detail;
      if (typeof detail === "string" && detail.trim()) return detail;
      if (Array.isArray(detail)) {
        const messages: string[] = detail.map(formatValidationError).filter(Boolean);
        if (messages.length) return messages.join("; ");
      }
    }
  } catch {
    // Proxy error pages can be HTML instead of the API's JSON error contract.
  }
  return `Request failed (HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ""}).`;
}

const validationFieldLabels: Readonly<Record<string, string>> = {
  serial_alias: "Controller serial",
  tenant_id: "Vendor",
  script_path: "Berry script path",
  script_config: "Berry script config",
  meters: "Meters",
  modbus_id: "Modbus ID",
  feeder_id: "Feeder",
  site_id: "Site ID",
  ntp_server: "NTP server",
  cellular_apn: "Primary APN",
  cellular_sim2_apn: "Secondary APN",
};

function formatValidationError(value: unknown): string {
  if (typeof value !== "object" || value === null) return "";
  const error: Record<string, unknown> = value as Record<string, unknown>;
  if (typeof error.msg !== "string" || !error.msg.trim()) return "";
  const location: unknown[] = Array.isArray(error.loc) ? error.loc : [];
  const fields: string[] = location
    .filter((field) => field !== "body" && field !== "query" && field !== "path")
    .map((field: unknown): string => {
      if (typeof field === "number") return `item ${field + 1}`;
      if (typeof field !== "string") return "";
      return validationFieldLabels[field] ?? field.replace(/_/g, " ");
    })
    .filter(Boolean);
  return fields.length ? `${fields.join(" / ")}: ${error.msg}` : error.msg;
}

let refreshRequest: Promise<LoginResponse> | null = null;

async function refreshSession(): Promise<LoginResponse> {
  if (refreshRequest) return refreshRequest;
  const refreshToken = readRefreshToken();
  if (!refreshToken) throw new ApiError(401, "session refresh is not available");
  refreshRequest = (async () => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) throw new ApiError(response.status, await readErrorDetail(response));
    return (await response.json()) as LoginResponse;
  })();
  try {
    const tokens = await refreshRequest;
    writeAuthToken(tokens.id_token);
    writeRefreshToken(tokens.refresh_token);
    return tokens;
  } finally {
    refreshRequest = null;
  }
}

function expireSession(): void {
  clearAuthTokens();
  window.dispatchEvent(new Event("esumei:auth-expired"));
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  allowRefreshRetry = true,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  const token = readAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    const previewSubject = readPreviewSubject();
    if (previewSubject) {
      headers["X-Preview-Subject"] = previewSubject;
    }
  }
  const organizationId = readOrganizationId();
  if (organizationId) headers["X-Organization-Id"] = organizationId;

  if (options.body !== undefined || options.rawBody !== undefined) {
    headers["Content-Type"] = options.contentType ?? "application/json";
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? "GET",
      headers,
      body:
        options.rawBody !== undefined
          ? options.rawBody
          : options.body === undefined
            ? undefined
            : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : "network request failed";
    throw new ApiError(0, `Request to ${API_BASE_URL}${path} failed: ${detail}`);
  }

  if (!response.ok) {
    if (response.status === 401 && token && allowRefreshRetry) {
      try {
        await refreshSession();
        return request<T>(path, options, false);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          expireSession();
        }
        if (!(error instanceof ApiError) || error.status === 401 || error.status === 403) {
          throw error;
        }
      }
    } else if (response.status === 401 && token) {
      expireSession();
    }
    throw new ApiError(response.status, await readErrorDetail(response));
  }
  if (response.status === 204) {
    return undefined as T;
  }
  if (options.responseType === "blob") return (await response.blob()) as T;
  return (await response.json()) as T;
}

export type ReportingReportView = {
  report: Exclude<GrafanaReport, "help">;
  filters: Omit<GrafanaReportFilters, "from" | "to">;
};

export type ReportingDefinition = {
  template: "telemetry-readings" | "executive-report";
  source: "postgres" | "archive";
  feeder_ids: string[];
  columns: string[];
  timezone: string;
  recipients: string[];
  report_view?: ReportingReportView | null;
};
export type ReportingRun = {
  id: string;
  template?: ReportingDefinition["template"];
  format?: "csv" | "xlsx";
  filename?: string;
  feeder_ids?: string[];
  report_view?: ReportingReportView | null;
  report_url?: string | null;
  report_link_unavailable_reason?: string | null;
  is_test?: boolean;
  source_test_subscription_id?: string | null;
  source_test_subscription_revision?: number | null;
  status: string;
  source: "postgres" | "archive";
  from_time: string;
  to_time: string;
  timezone: string;
  created_at: string;
  row_count: number | null;
  byte_count: number | null;
  error: string | null;
  download_available: boolean;
  recipients: string[];
  artifact_expires_at?: string | null;
  artifact_sha256?: string | null;
  data_cutoff?: string | null;
  source_snapshot?: {
    revision?: number;
    validated_at?: string;
    publication_asof?: string;
    dashboard_uid?: string;
    resource_sha256?: string;
    queried_at?: string;
  } | null;
  deliveries?: ReportingDelivery[];
};
export type ReportingDelivery = {
  recipient: string;
  status: string;
  error?: string | null;
  attempt_id?: string | null;
  updated_at?: string | null;
  retry_at?: string | null;
};
export type ReportingSubscriptionInput = ReportingDefinition & {
  name: string;
  enabled: boolean;
  period?: ReportingPeriod;
  publication_delay_minutes?: number;
  schedule: { frequency: "daily" | "weekly" | "monthly"; time: string; weekday: number | null; month_day?: number | null };
};
export type ReportingPeriod = "previous_day" | "previous_week" | "previous_month";
export type ReportingSchedulePreview = {
  next_run_at: string;
  scheduled_for: string;
  from_time: string;
  to_time: string;
  timezone: string;
  period: ReportingPeriod;
  publication_delay_minutes: number;
  test_from_time: string;
  test_to_time: string;
};
export type ReportingSubscription = ReportingSubscriptionInput & { id: string; next_run_at: string | null };
export type ReportingCapabilities = {
  enabled: boolean;
  templates: { id: string; name: string }[];
  sources: ("postgres" | "archive")[];
  columns: { id: string; label: string }[];
  limits: { max_rows: number; max_bytes: number; max_range_days: number };
  email_enabled: boolean;
  test_delivery_only?: boolean;
  sender: string | null;
};
export const reportingApi = {
  capabilities: (signal?: AbortSignal) => request<ReportingCapabilities>("/reporting/capabilities", { signal }),
  runs: (signal?: AbortSignal) => request<ReportingRun[]>("/reporting/runs", { signal }),
  createRun: (body: ReportingDefinition & { from_time: string; to_time: string }) =>
    request<ReportingRun>("/reporting/runs", { method: "POST", body }),
  download: (id: string) => request<Blob>(`/reporting/runs/${encodeURIComponent(id)}/download`, { responseType: "blob" }),
  subscriptions: (signal?: AbortSignal) => request<ReportingSubscription[]>("/reporting/subscriptions", { signal }),
  testSubscription: (id: string) =>
    request<ReportingRun>(`/reporting/subscriptions/${encodeURIComponent(id)}/test`, { method: "POST" }),
  previewSubscription: (body: ReportingSubscriptionInput) =>
    request<ReportingSchedulePreview>("/reporting/subscriptions/preview", { method: "POST", body }),
  saveSubscription: (body: ReportingSubscriptionInput, id?: string) =>
    request<ReportingSubscription>(`/reporting/subscriptions${id ? `/${encodeURIComponent(id)}` : ""}`, { method: id ? "PUT" : "POST", body }),
};

// ---- Types

export type AuthMode = {
  preview_auth_mode: boolean;
  local_auth_bypass_mode: boolean;
  token_audience: string;
};

export type LoginResponse = {
  id_token: string;
  refresh_token: string;
  expires_in: number;
  email: string;
};

export type MeResponse = {
  user_id: number;
  email: string;
  display_name: string;
  roles: string[];
  tenant_id: number | null;
  active_organization_kind: string | null;
  capabilities: string[];
  organizations: Array<{ tenant_id: number; name: string | null; role: string }>;
};

export type PreviewUser = {
  user_id: number;
  identity_subject: string;
  email: string;
  display_name: string;
  role: string;
  tenant_id: number | null;
  tenant_name: string | null;
};

export type Tenant = {
  tenant_id: number;
  name: string;
  external_key: string;
  kind: "vendor" | "distribution_company";
};

export type UserSummary = {
  user_id: number;
  email: string;
  display_name: string;
  memberships: Array<{
    tenant_id: number | null;
    name: string | null;
    role: Role;
  }>;
};

export type UserMembershipResult = {
  user_id: number;
  email: string;
  display_name: string;
  role: Role;
  tenant_id: number | null;
  tenant_name: string | null;
};

export type Role = "platform_admin" | "tenant_admin" | "tenant_viewer";

export type CreateUserRequest = {
  email: string;
  display_name: string;
  password: string;
  memberships: MembershipAssignment[];
};

export type MembershipAssignment = {
  role: Role;
  tenant_id: number | null;
};

export type ControllerShare = {
  customer_tenant_id: number;
  customer_name: string;
};

export type UpdateUserRequest = {
  display_name: string;
  role: Role;
  tenant_id: number | null;
};

export type DeviceSummary = {
  device_id: string;
  serial_alias: string;
  controller_id: string;
  tenant_id: number | null;
  tenant_name: string | null;
  site_id: string;
  provider_source: string;
  provisioning_status: string;
  connection_status: string;
  tenant_visible: boolean;
  latest_telemetry_at: string | null;
  status_bits: number | null;
  v_l12_x100: number | null;
  v_l23_x100: number | null;
  v_l31_x100: number | null;
  a_l1_x100: number | null;
  a_l2_x100: number | null;
  a_l3_x100: number | null;
  active_power_inst_x10: number | null;
  reactive_power_inst_x10: number | null;
  apparent_power_inst_x10: number | null;
  power_factor_x1000: number | null;
  frequency_x100: number | null;
  active_energy_tot_wh: number | null;
};

export type DashboardOverview = {
  total_provisioned_devices: number;
  total_visible_devices: number;
  total_meters_online: number;
  dar_percent: number;
  total_active_power_inst_x10: number;
  total_energy_consumed_wh: number;
  avg_frequency_x100: number;
  avg_power_factor_x1000: number;
};

export type GrafanaEmbedUrl = {
  url: string;
};

export type TelemetryPoint = {
  ingested_at: string;
  status_bits: number | null;
  v_l12_x100: number | null;
  v_l23_x100: number | null;
  v_l31_x100: number | null;
  a_l1_x100: number | null;
  a_l2_x100: number | null;
  a_l3_x100: number | null;
  active_power_inst_x10: number | null;
  reactive_power_inst_x10: number | null;
  apparent_power_inst_x10: number | null;
  power_factor_x1000: number | null;
  frequency_x100: number | null;
  active_energy_tot_wh: number | null;
};

export type ControllerTelemetryPoint = TelemetryPoint & {
  meter_id: string;
};

export type FeederTelemetryPoint = TelemetryPoint & {
  feeder_id: number;
  controller_id: string;
  device_id: string;
  meter_id: number;
  device_timestamp_at: string;
};

export type ResolveAliasResponse = {
  serial_alias: string;
  device_id: string;
  controller_id: string;
};

export type ControllerClaimRequest = {
  tenant_id: number;
  serial_alias: string;
  site_id?: string;
  script_path: string;
  ntp_server: string;
  script_config: Record<string, unknown>;
  meters: Array<{
    slot: number;
    modbus_id: number;
    feeder_id: number | null;
    custom_ct_primary?: number;
    custom_ct_secondary?: number;
    custom_vt_primary?: number;
    custom_vt_secondary?: number;
  }>;
};

export type ExistingControllerLinkRequest = {
  tenant_id: number;
  serial_alias: string;
  meters: ControllerClaimRequest["meters"];
};

export type RatioVerificationFields = {
  ct_verification_state?: "unverified" | "verified";
  ct_verification_source?: string;
  vt_verification_state?: "unverified" | "verified";
  vt_verification_source?: string;
};

export type RatioVerificationRequest = {
  ratio: "ct" | "vt";
  state: "unverified" | "verified";
  source: string;
  primary: number;
  secondary: number;
};

export type Feeder = RatioVerificationFields & {
  feeder_id: number;
  is_unassigned_placeholder?: boolean;
  // Computed compatibility alias for the canonical feeder_name.
  display_name?: string | null;
  source_voltage?: string | null;
  upstream_feeder_name?: string | null;
  upstream_voltage_kv?: number | null;
  source_voltage_kv?: number | null;
  voltage_kv?: number | null;
  feeder_location?: string | null;
  major_streets?: string | null;
  served_states?: string[] | null;
  regulators?: string[] | null;
  nerc_service_classes?: string[] | null;
  metered_customers?: number | null;
  unmetered_customers?: number | null;
  iot_vendor?: string | null;
  iot_phase?: string | null;
  iot_installed?: boolean | null;
  source_metadata?: Record<string, unknown>;
  feeder_type?: string | null;
  feeder_type_detail?: string | null;
  external_key: string;
  feeder_name: string;
  distribution_company_id: number;
  distribution_company_name: string;
  substation_id: number;
  substation_name: string;
  latitude: number | null;
  longitude: number | null;
  power_transformer_id: string;
  power_transformer_capacity_mva: number | null;
  feeder_band: string;
  feeder_voltage: string;
  ct_vt_condition: string;
  ct_primary: number;
  ct_secondary: number;
  vt_primary: number;
  vt_secondary: number;
  station_dc_nominal_v: number | null;
  station_dc_min_v: number | null;
  station_dc_max_v: number | null;
  preferred_network: string;
  installation_type_available: string;
  remarks: string;
  revision: number;
  assignments: FeederAssignment[];
};

export type Substation = {
  substation_id: number;
  address_line?: string | null;
  city?: string | null;
  state?: string | null;
  // Computed compatibility alias for the canonical name.
  base_name?: string | null;
  station_type?: "TS" | "ISS" | null;
  voltage_kv?: number | null;
  served_states?: string[] | null;
  tcn_region?: string | null;
  tcn_subregion?: string | null;
  transmission_station?: string | null;
  injection_substation?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  distribution_company_id: number;
  distribution_company_name: string;
  external_key: string;
  name: string;
  is_test: boolean;
};

export type FeederAssignment = {
  controller_meter_id: number;
  device_id: string;
  serial_alias: string;
  controller_display_name: string;
  tenant_name: string | null;
  meter_label: string;
  slot: number;
  modbus_id: number;
};

export type FeederHistoryInterval = {
  feeder_id: number;
  controller_id: string;
  device_id: string;
  meter_id: number;
  valid_from: string | null;
  valid_to: string | null;
  recorded_at: string | null;
  closed_recorded_at: string | null;
};

export type ControllerSummary = {
  device_id: string;
  serial_alias: string;
  controller_id: string;
  tenant_id: number | null;
  tenant_name: string | null;
  site_id: string;
  display_name: string;
  provider_source: string;
  provisioning_status: string;
  connection_status: string;
  latest_telemetry_at: string | null;
  meter_count: number | null;
};

export type BrowsePage<T> = { items: T[]; total: number; page: number; page_size: number };
export type BrowseParams = Record<string, string | number | undefined>;
export type ControllerBrowseOptions = {
  vendors: { tenant_id: number; name: string }[];
  sites: string[];
  connection_statuses: string[];
  provisioning_statuses: string[];
};

function browseQuery(params: BrowseParams): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  return query.toString();
}

export type ControllerMeter = RatioVerificationFields & {
  controller_meter_id: number;
  device_id: string;
  updated_at: string;
  slot: number;
  modbus_id: number;
  enabled: boolean;
  label: string;
  source_mode: "feeder" | "custom";
  feeder_id: number | null;
  feeder_revision: number | null;
  resolved_ct_primary: number | null;
  resolved_ct_secondary: number | null;
  resolved_vt_primary: number | null;
  resolved_vt_secondary: number | null;
  custom_ct_primary: number | null;
  custom_ct_secondary: number | null;
  custom_vt_primary: number | null;
  custom_vt_secondary: number | null;
};

export type ConfigScriptSnapshot = {
  m?: Array<[number, Record<string, number>]>;
  poll_ms?: number;
  sentinel_poll_ms?: number;
};

export type ConfigSnapshot = {
  site?: { site_id: string };
  time?: { ntp_server: string };
  cellular?: { apn: string; sim2_apn: string };
  berry?: { script_path?: string; script_config?: ConfigScriptSnapshot };
};

export type ControllerDetail = {
  device_id: string;
  serial_alias: string;
  controller_id: string;
  tenant_id: number | null;
  tenant_name: string | null;
  site_id: string;
  provider_source: string;
  display_name: string;
  provisioning_status: string;
  connection_status: string;
  latest_telemetry_at: string | null;
  desired_config_id: number | null;
  reported_config_id: number | null;
  reported_state: string | null;
  reported_error?: string | null;
  reported_failure_reason?: string | null;
  reported_failure_count?: number | null;
  reported_at?: string | null;
  reported_cellular_apn?: string | null;
  reported_cellular_sim2_apn?: string | null;
  reported_config?: ConfigSnapshot | null;
  desired_config?: ConfigSnapshot | null;
  desired_updated_at?: string | null;
  desired_publish_status?: string | null;
  desired_published_at?: string | null;
  pending_publish_config_id: number | null;
  last_reason: string | null;
  cellular_apn: string;
  cellular_sim2_apn: string;
  ntp_server: string;
  script_path: string;
  poll_ms: number;
  sentinel_poll_ms: number;
  meters: ControllerMeter[];
};

export type FeederUpsertRequest = Omit<
  Feeder,
  | "feeder_id"
  | "is_unassigned_placeholder"
  | "source_voltage"
  | "distribution_company_id"
  | "distribution_company_name"
  | "substation_name"
  | "revision"
  | "assignments"
>;

export type SubstationUpsertRequest = Omit<Substation, "substation_id" | "distribution_company_name" | "is_test" | "name"> & { name?: string };

export type GridOptions = Record<
  "station_types" | "voltage_kv" | "feeder_bands" | "feeder_types" | "states" |
  "regulators" | "nerc_service_classes" | "iot_vendors" | "iot_phases" | "installation_types",
  string[]
>;

export type ControllerSettingsUpdateRequest = {
  display_name: string;
  site_id: string;
  ntp_server: string;
  script_path: string;
};

export type ControllerMeterUpsertRequest = {
  slot: number;
  modbus_id: number;
  enabled: boolean;
  label: string;
  source_mode: "feeder" | "custom";
  feeder_id: number | null;
  custom_ct_primary: number | null;
  custom_ct_secondary: number | null;
  custom_vt_primary: number | null;
  custom_vt_secondary: number | null;
};

export type FeederAssignmentSourceRequest = {
  source_device_id: string;
  source_meter_id: number;
  expected_source_updated_at: string;
  effective_at: string | null;
};

export type FeederReplacementRequest = FeederAssignmentSourceRequest & {
  expected_feeder_revision: number;
  target_device_id: string;
  target_meter_id: number;
  expected_target_updated_at: string;
  expected_target_feeder_id: number | null;
  confirm_move: boolean;
};

export type ControllerCompilePreview = {
  config_id: number;
  payload_size: number;
  script_config_json: string;
  cloud_model: Record<string, unknown>;
  device_shape: Record<string, unknown>;
};

// ---- Endpoints

export const api = {
  // auth
  authMode: () => request<AuthMode>("/auth/mode"),
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", { method: "POST", body: { email, password } }),
  me: () => request<MeResponse>("/me"),
  previewUsers: () => request<PreviewUser[]>("/preview/users"),

  // dashboard
  dashboard: () => request<DashboardOverview>("/dashboard"),
  analyticsFeeders: (signal?: AbortSignal, organizationId?: number) =>
    request<AnalyticsFeeder[]>(`/analytics/feeders${organizationId === undefined ? "" : `?organization_id=${organizationId}`}`, { signal }),
  grafanaReportEmbed: (
    report: GrafanaReport,
    filters: GrafanaReportFilters,
    signal?: AbortSignal,
    organizationId?: number,
  ) =>
    request<GrafanaEmbedUrl>(`/grafana/embed/reports/${report}${organizationId === undefined ? "" : `?organization_id=${organizationId}`}`, {
      method: "POST",
      body: filters,
      signal,
    }),
  grafanaEmbed: () => request<GrafanaEmbedUrl>("/grafana/embed", { method: "POST" }),
  grafanaComplianceEmbed: () =>
    request<GrafanaEmbedUrl>("/grafana/embed/compliance", { method: "POST" }),
  grafanaControllerEmbed: (deviceId: string) =>
    request<GrafanaEmbedUrl>(`/grafana/embed/controllers/${encodeURIComponent(deviceId)}`, {
      method: "POST",
    }),
  grafanaDiagnosticsEmbed: (deviceId: string) =>
    request<GrafanaEmbedUrl>(
      `/grafana/embed/controllers/${encodeURIComponent(deviceId)}/diagnostics`,
      { method: "POST" },
    ),

  // devices
  devices: () => request<DeviceSummary[]>("/devices"),
  device: (deviceId: string) => request<DeviceSummary>(`/devices/${encodeURIComponent(deviceId)}`),
  deviceTelemetry: (deviceId: string) =>
    request<TelemetryPoint[]>(`/devices/${encodeURIComponent(deviceId)}/telemetry/series`),

  // controllers
  controllers: () => request<ControllerSummary[]>("/controllers"),
  browseControllers: (params: BrowseParams) => request<BrowsePage<ControllerSummary>>(`/controllers/browse?${browseQuery(params)}`),
  controllerBrowseOptions: () => request<ControllerBrowseOptions>("/controllers/browse/options"),
  controller: (deviceId: string) =>
    request<ControllerDetail>(`/controllers/${encodeURIComponent(deviceId)}`),
  updateControllerSettings: (deviceId: string, body: ControllerSettingsUpdateRequest) =>
    request<ControllerDetail>(`/controllers/${encodeURIComponent(deviceId)}/settings`, {
      method: "PUT",
      body,
    }),
  controllerShares: (deviceId: string) =>
    request<ControllerShare[]>(`/controllers/${encodeURIComponent(deviceId)}/shares`),
  shareController: (deviceId: string, customerTenantId: number) =>
    request<void>(`/controllers/${encodeURIComponent(deviceId)}/shares`, {
      method: "PUT",
      body: { customer_tenant_id: customerTenantId },
    }),
  revokeControllerShare: (deviceId: string, customerTenantId: number) =>
    request<void>(`/controllers/${encodeURIComponent(deviceId)}/shares/${customerTenantId}`, {
      method: "DELETE",
    }),
  createControllerMeter: (deviceId: string, body: ControllerMeterUpsertRequest) =>
    request<ControllerMeter>(`/controllers/${encodeURIComponent(deviceId)}/meters`, {
      method: "POST",
      body,
    }),
  updateControllerMeter: (
    deviceId: string,
    controllerMeterId: number,
    body: ControllerMeterUpsertRequest,
  ) =>
    request<ControllerMeter>(
      `/controllers/${encodeURIComponent(deviceId)}/meters/${controllerMeterId}`,
      {
        method: "PUT",
        body,
      },
    ),
  deleteControllerMeter: (deviceId: string, controllerMeterId: number) =>
    request<void>(`/controllers/${encodeURIComponent(deviceId)}/meters/${controllerMeterId}`, {
      method: "DELETE",
    }),
  controllerCompilePreview: (deviceId: string) =>
    request<ControllerCompilePreview>(
      `/controllers/${encodeURIComponent(deviceId)}/compile-preview`,
    ),
  controllerCompilePreviewDraft: (deviceId: string, body: ControllerSettingsUpdateRequest) =>
    request<ControllerCompilePreview>(
      `/controllers/${encodeURIComponent(deviceId)}/compile-preview`,
      { method: "POST", body },
    ),
  controllerTelemetry: (deviceId: string) =>
    request<ControllerTelemetryPoint[]>(
      `/controllers/${encodeURIComponent(deviceId)}/telemetry/series`,
    ),
  queueControllerDesired: (deviceId: string) =>
    request<ControllerCompilePreview>(
      `/controllers/${encodeURIComponent(deviceId)}/queue-desired`,
      { method: "POST" },
    ),

  // feeders
  gridOptions: () => request<GridOptions>("/grid/options"),
  feeders: () => request<Feeder[]>("/feeders"),
  feeder: (feederId: number) => request<Feeder>(`/feeders/${feederId}`),
  feederHistory: (feederId: number) =>
    request<FeederHistoryInterval[]>(`/feeders/${feederId}/history`),
  feederTelemetry: (feederId: number, start: string, end: string) =>
    request<FeederTelemetryPoint[]>(`/feeders/${feederId}/telemetry/series?${new URLSearchParams({ start, end, limit: "1000" })}`),
  replaceFeederAssignment: (feederId: number, body: FeederReplacementRequest) =>
    request<{ feeder_id: number; replaced_meter_id: number; replacement_meter_id: number }>(
      `/feeders/${feederId}/assignments/replace`, { method: "POST", body },
    ),
  detachFeederAssignment: (feederId: number, body: FeederAssignmentSourceRequest) =>
    request<{ feeder_id: number; detached_meter_id: number }>(
      `/feeders/${feederId}/assignments/detach`, { method: "POST", body },
    ),
  browseFeeders: (params: BrowseParams) => request<BrowsePage<Feeder>>(`/feeders/browse?${browseQuery(params)}`),
  browseSubstations: (params: BrowseParams) => request<BrowsePage<Substation>>(`/substations/browse?${browseQuery(params)}`),
  substations: (distributionCompanyId?: number) =>
    request<Substation[]>(
      distributionCompanyId === undefined
        ? "/substations"
        : `/substations?distribution_company_id=${distributionCompanyId}`,
    ),
  createSubstation: (body: SubstationUpsertRequest) =>
    request<Substation>("/substations", { method: "POST", body }),
  updateSubstation: (id: number, body: SubstationUpsertRequest) =>
    request<Substation>(`/substations/${id}`, { method: "PUT", body }),
  importSubstationsCsv: (csv: string) =>
    request<CsvImportResult>("/substations/import", {
      method: "POST",
      rawBody: csv,
      contentType: "text/csv",
    }),
  importFeedersCsv: (csv: string) =>
    request<CsvImportResult>("/feeders/import", {
      method: "POST",
      rawBody: csv,
      contentType: "text/csv",
    }),
  previewGridImport: (kind: GridImportKind, content: string) =>
    request<GridImportReview>(`/grid-import/${kind}/preview`, {
      method: "POST", body: { content },
    }),
  commitGridImport: (kind: GridImportKind, content: string, fingerprint: string) =>
    request<CsvImportResult>(`/grid-import/${kind}/commit`, {
      method: "POST", body: { content, fingerprint },
    }),
  createFeeder: (body: FeederUpsertRequest) =>
    request<Feeder>("/feeders", { method: "POST", body }),
  updateFeeder: (feederId: number, body: FeederUpsertRequest) =>
    request<Feeder>(`/feeders/${feederId}`, { method: "PUT", body }),
  verifyFeederRatio: (feederId: number, body: RatioVerificationRequest) =>
    request<void>(`/feeders/${feederId}/ratio-verification`, { method: "POST", body }),
  verifyCustomMeterRatio: (deviceId: string, meterId: number, body: RatioVerificationRequest) =>
    request<void>(`/controllers/${encodeURIComponent(deviceId)}/meters/${meterId}/ratio-verification`, {
      method: "POST", body,
    }),
  deleteFeeder: (feederId: number) => request<void>(`/feeders/${feederId}`, { method: "DELETE" }),

  // tenants
  tenants: () => request<Tenant[]>("/tenants"),
  browseTenants: (params: BrowseParams) => request<BrowsePage<Tenant>>(`/tenants/browse?${browseQuery(params)}`),
  distributionCompanies: () => request<Tenant[]>("/distribution-companies"),
  createTenant: (body: { name: string; external_key: string; kind: Tenant["kind"] }) =>
    request<Tenant>("/tenants", { method: "POST", body }),
  updateTenant: (tenantId: number, body: { name: string; external_key: string; kind: Tenant["kind"] }) =>
    request<Tenant>(`/tenants/${tenantId}`, { method: "PUT", body }),
  deleteTenant: (tenantId: number) => request<void>(`/tenants/${tenantId}`, { method: "DELETE" }),

  // users
  users: () => request<UserSummary[]>("/users"),
  browseUsers: (params: BrowseParams) => request<BrowsePage<UserSummary>>(`/users/browse?${browseQuery(params)}`),
  createUser: (body: CreateUserRequest) => request<UserMembershipResult>("/users", { method: "POST", body }),
  updateUser: (userId: number, body: UpdateUserRequest) =>
    request<UserMembershipResult>(`/users/${userId}`, { method: "PUT", body }),
  updateUserMemberships: (userId: number, body: { memberships: MembershipAssignment[] }) =>
    request<UserSummary>(`/users/${userId}/memberships`, { method: "PUT", body }),
  deleteUser: (userId: number) => request<void>(`/users/${userId}`, { method: "DELETE" }),

  // controller claim
  resolveAlias: (serialAlias: string) =>
    request<ResolveAliasResponse>("/provisioning/resolve-alias", {
      method: "POST",
      body: { serial_alias: serialAlias },
    }),
  claimDevice: (body: ControllerClaimRequest) =>
    request<ResolveAliasResponse>("/provisioning/claims", { method: "POST", body }),
  linkExistingController: (body: ExistingControllerLinkRequest) =>
    request<ResolveAliasResponse>("/provisioning/links", { method: "POST", body }),
};
